import React, { useEffect, useState } from 'react';
import axios from '../utility/customAxios';
import { format } from 'date-fns';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react';
import { cn } from './ui/lib/utils';
import { usePreserveQueryParamsNavigate } from './ui/lib/utils';

interface AlertFire {
    uuid: string;
    alertUuid: string;
    alertName: string;
    startTime: number;
    endTime?: number;
    message: string;
    kubernetesEnvironment?: string;
    kubernetesService?: string;
}

interface AlertFiresTimelineProps {
    startTime: number;
    endTime: number;
    className?: string;
}

interface AlertFireGroup {
    position: number;
    fires: AlertFire[];
}

export const AlertFiresTimeline: React.FC<AlertFiresTimelineProps> = ({ startTime, endTime, className }) => {
    const [alertFires, setAlertFires] = useState<AlertFire[]>([]);
    const [mutedAlertIds, setMutedAlertIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
    const navigate = usePreserveQueryParamsNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Fetch both alert fires and alerts in parallel
                const [alertFiresResponse, alertsResponse] = await Promise.all([
                    axios.get(`/api/v1/allAlertFires`, {
                        params: {
                            startTime: Math.floor(startTime),
                            endTime: Math.floor(endTime)
                        }
                    }),
                    axios.get(`/api/v1/searchAlerts`)
                ]);

                // Create a set of muted alert IDs
                const muted = new Set<string>();
                if (alertsResponse.data.alerts) {
                    alertsResponse.data.alerts.forEach((alert: any) => {
                        if (alert.muted) {
                            muted.add(alert.uuid);
                        }
                    });
                }
                setMutedAlertIds(muted);

                // Filter out alert fires from muted alerts
                const allFires = alertFiresResponse.data.alertFires || [];
                const unmutedFires = allFires.filter((fire: AlertFire) => !muted.has(fire.alertUuid));
                setAlertFires(unmutedFires);
            } catch (error) {
                console.error('Error fetching data:', error);
                setAlertFires([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [startTime, endTime]);

    if (isLoading) {
        return (
            <div className="w-full h-24 bg-backgroundmedium rounded-lg animate-pulse mb-4" />
        );
    }

    if (alertFires.length === 0) {
        return (
            <div className={cn("w-full bg-backgroundmedium rounded-lg p-4 border", className)}>
                <div className="text-sm font-medium text-textdark mb-1">Alert Fire Timeline</div>
                <div className="flex items-center justify-center h-12 text-sm text-textdark">
                    No alert fires in the selected time period
                </div>
            </div>
        );
    }

    // Calculate timeline position for each fire
    const timeRange = endTime - startTime;
    const getPositionPercentage = (time: number) => {
        return ((time - startTime) / timeRange) * 100;
    };

    // Group nearby alerts (within 2% of timeline width)
    const groupThreshold = 2; // percentage
    const alertGroups: AlertFireGroup[] = [];
    
    const sortedFires = [...alertFires].sort((a, b) => a.startTime - b.startTime);
    
    sortedFires.forEach((fire) => {
        // For fires that started before the timeline window, position them at the start
        const fireStartTime = fire.startTime < startTime ? startTime : fire.startTime;
        const firePosition = getPositionPercentage(fireStartTime);
        
        const existingGroup = alertGroups.find(group => 
            Math.abs(group.position - firePosition) <= groupThreshold
        );
        
        if (existingGroup) {
            existingGroup.fires.push(fire);
        } else {
            alertGroups.push({
                position: firePosition,
                fires: [fire]
            });
        }
    });

    // Check if time range spans multiple days
    const startDate = new Date(startTime * 1000);
    const endDate = new Date(endTime * 1000);
    const spansDays = startDate.toDateString() !== endDate.toDateString();
    const timeFormat = spansDays ? 'MMM dd, HH:mm' : 'HH:mm';

    return (
        <TooltipProvider delayDuration={0}>
            <div className={cn("w-full bg-backgroundmedium rounded-lg p-4 border", className)}>
                <div className="text-sm font-medium text-textdark mb-1">Alert Fire Timeline</div>
                
                <div className="relative h-12">
                    {/* Timeline background line */}
                    <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-700 -translate-y-1/2" />
                    
                    {/* Time markers */}
                    <div className="absolute top-0 left-0 text-xs text-textdark">
                        {format(new Date(startTime * 1000), timeFormat)}
                    </div>
                    <div className="absolute top-0 right-0 text-xs text-textdark">
                        {format(new Date(endTime * 1000), timeFormat)}
                    </div>
                    
                    {/* Alert fire points */}
                    {alertGroups.map((group, groupIndex) => {
                        const hasActiveFire = group.fires.some(fire => !fire.endTime);
                        const groupId = `group-${groupIndex}`;
                        
                        return (
                            <Tooltip key={groupId} open={openTooltipId === groupId} onOpenChange={(open) => setOpenTooltipId(open ? groupId : null)}>
                                <TooltipTrigger asChild>
                                    <div
                                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer"
                                        style={{ left: `${group.position}%` }}
                                    >
                                        <div className={`relative w-4 h-4 rounded-full ${hasActiveFire ? 'bg-red-600 animate-pulse' : 'bg-red-500'} hover:scale-125 transition-transform`}>
                                            <AlertTriangleIcon className="w-4 h-4 text-white p-0.5" />
                                            {group.fires.length > 1 && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-700 rounded-full flex items-center justify-center text-white text-[10px] font-bold border border-red-900">
                                                    {group.fires.length}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent 
                                    side="top" 
                                    className="bg-backgroundlight border border-gray-700 p-3 max-w-sm max-h-96 overflow-y-auto"
                                >
                                    <div className="space-y-3">
                                        {group.fires.length > 1 && (
                                            <div className="text-sm font-semibold text-white border-b border-gray-700 pb-2">
                                                {group.fires.length} Alerts
                                            </div>
                                        )}
                                        {group.fires.map((fire, fireIndex) => {
                                            const duration = fire.endTime ? fire.endTime - fire.startTime : 0;
                                            const durationMinutes = Math.round(duration / 60);
                                            
                                            return (
                                                <div key={fire.uuid} className={fireIndex > 0 ? "pt-3 border-t border-gray-700" : ""}>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div className="font-semibold text-white text-sm">{fire.alertName}</div>
                                                            <button
                                                                onClick={() => {
                                                                    // Navigate with 15 minutes before and after the alert fire
                                                                    const alertTime = fire.startTime * 1000; // Convert to milliseconds
                                                                    const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
                                                                    const startTimeMs = alertTime - fifteenMinutes;
                                                                    const endTimeMs = alertTime + fifteenMinutes;
                                                                    navigate(`/alert?alertId=${fire.alertUuid}&startEnd=${startTimeMs},${endTimeMs}`);
                                                                }}
                                                                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                                                            >
                                                                View
                                                                <ExternalLinkIcon className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                        <div className="text-xs text-textdark">
                                                            {fire.startTime < startTime ? (
                                                                <>Started before {format(new Date(startTime * 1000), 'MMM dd, HH:mm:ss')}</>
                                                            ) : (
                                                                format(new Date(fire.startTime * 1000), 'MMM dd, HH:mm:ss')
                                                            )}
                                                        </div>
                                                        {fire.endTime ? (
                                                            <div className="text-xs text-green-400">
                                                                ✓ Resolved • Duration: {durationMinutes} min
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-red-400">
                                                                ● Currently Firing
                                                            </div>
                                                        )}
                                                        {(fire.kubernetesEnvironment || fire.kubernetesService) && (
                                                            <div className="space-y-1">
                                                                {fire.kubernetesEnvironment && (
                                                                    <div className="text-xs text-textmedium">
                                                                        <span className="text-textdark">Environment:</span> {fire.kubernetesEnvironment}
                                                                    </div>
                                                                )}
                                                                {fire.kubernetesService && (
                                                                    <div className="text-xs text-textmedium">
                                                                        <span className="text-textdark">Service:</span> {fire.kubernetesService}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
                
                <div className="flex items-center gap-4 mt-0 text-xs text-textdark">
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-red-600 animate-pulse" />
                        <span>Currently Firing</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>Resolved</span>
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};